"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Star } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  name: "Jane D.",
  role: "Landlord",
  quote: "LiveAmazing made letting my property hassle-free and profitable. Their attention to detail is unmatched.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/avatar_jane_landlord.png"
}, {
  name: "Robert Fox",
  role: "Property Investor",
  quote: "I've worked with many agencies, but none compare to the efficiency and transparency of this team.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/avatar_robert_investor.png"
}, {
  name: "Sarah Jenkins",
  role: "Landlord",
  quote: "From valuation to tenant placement, the process was seamless. Highly recommended for peace of mind.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/avatar_sarah_landlord.png"
}, {
  name: "Michael Chen",
  role: "Portfolio Owner",
  quote: "Their property management service has saved me countless hours. The rent collection rate is phenomenal.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/avatar_michael_owner.png"
}, {
  name: "Emily W.",
  role: "Landlord",
  quote: "Professional, reliable, and truly expert in the local market. My property was let in record time.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/avatar_emily_landlord.png"
}];
const stats = [{
  value: "99%",
  label: "Rent Collection Rate"
}, {
  value: "14 Days",
  label: "Average Let Time"
}, {
  value: "500+",
  label: "Properties Managed"
}, {
  value: "24/7",
  label: "Landlord Support"
}];
export default function SuccessInNumbers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="success-in-numbers" ref={ref} className={`bg-background text-foreground py-20 md:py-32 overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4">
        {}
        <div className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif mb-4">
              Trusted by Landlords
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Join hundreds of satisfied property owners who trust us with their
              investments.
            </p>
          </div>

          <div className="relative px-4 md:px-12">
            <Carousel opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent className="-ml-4">
                {testimonials.map((testimonial, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                    <Card className="bg-card border-border/50 h-full">
                      <CardContent className="p-8 flex flex-col h-full justify-between">
                        <div>
                          <div className="flex gap-1 mb-4">
                            {[...Array(5)].map((_, starIndex) => <Star key={starIndex} className="w-4 h-4 fill-secondary text-secondary" />)}
                          </div>
                          <blockquote className="text-lg italic text-muted-foreground mb-6 leading-relaxed">
                            &ldquo;{testimonial.quote}&rdquo;
                          </blockquote>
                        </div>
                        <div className="flex items-center gap-4 mt-auto pt-4 border-t border-border/30">
                          <SafeHydrate><Avatar className="h-10 w-10 border border-border">
                            <AvatarImage src={testimonial.avatar} alt={testimonial.name} />
                            <AvatarFallback className="bg-secondary text-secondary-foreground">
                              {testimonial.name.split(" ").map(n => n[0]).join("")}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          <div>
                            <div className="font-semibold text-foreground">
                              {testimonial.name}
                            </div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wider">
                              {testimonial.role}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>)}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex -left-4 bg-background border-border hover:bg-secondary hover:text-secondary-foreground" />
              <CarouselNext className="hidden md:flex -right-4 bg-background border-border hover:bg-secondary hover:text-secondary-foreground" />
            </Carousel>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 text-center border-t border-border/30 pt-16">
          {stats.map((stat, i) => <div key={i} data-index={i} className="group">
              <div className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-secondary mb-4 transition-transform duration-500 group-hover:scale-105" style={{
            textShadow: "0 0 20px var(--color-secondary)",
            opacity: 0.9
          }}>
                {stat.value}
              </div>
              <div className="text-lg md:text-xl text-muted-foreground font-medium tracking-wide">
                {stat.label}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}